import type { BenefitInputs } from '../types/rulesEngine'
import type { EligibilityDashboardView } from '../services/eligibilityDashboardView'
import type { EligibilityServiceResult } from './getEligibilityByProductSlugs'

function parsePercent(raw: string | undefined): number {
  if (!raw?.trim()) return 0
  const n = Number.parseFloat(raw.replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

function yesNoFromCoverage(value: string | undefined): 'yes' | 'no' {
  if (!value?.trim()) return 'yes'
  const u = value.trim().toLowerCase()
  if (u === 'no' || u === 'not covered' || u === 'false' || u === 'n') return 'no'
  return 'yes'
}

function deductibleMetPct(total: number, remaining: number): number {
  if (total <= 0) return 100
  const accumulated = Math.max(0, total - remaining)
  return Math.min(100, Math.round((accumulated / total) * 100))
}

/**
 * Maps eligibility dashboard + optional service-level benefits into rules-engine inputs.
 */
export function mapEligibilityToBenefitInputs(
  dashboard: EligibilityDashboardView,
  serviceResult?: EligibilityServiceResult | null
): BenefitInputs {
  const fin = dashboard.financial
  const coinsurancePct =
    parsePercent(serviceResult?.coInsInNet) || parsePercent(dashboard.financial.coinsurance)
  const coveragePct = coinsurancePct > 0 ? Math.max(0, 100 - coinsurancePct) : 80

  const benefitCovered = serviceResult
    ? yesNoFromCoverage(serviceResult.coveredInNet ?? serviceResult.coveredOutNet)
    : dashboard.dme?.rows?.some((r) => /not covered/i.test(r.value))
      ? 'no'
      : 'yes'

  const copay =
    serviceResult?.coPayInNet != null
      ? Number.parseFloat(String(serviceResult.coPayInNet).replace(/[^0-9.-]/g, '')) || fin.copay
      : fin.copay

  const deductibleTotal =
    serviceResult?.deductible != null
      ? Number.parseFloat(String(serviceResult.deductible).replace(/[^0-9.-]/g, '')) || fin.deductible
      : fin.deductible

  const deductibleRemaining =
    serviceResult?.deductibleRemaining != null
      ? Number.parseFloat(String(serviceResult.deductibleRemaining).replace(/[^0-9.-]/g, '')) ||
        fin.deductibleRemaining
      : fin.deductibleRemaining

  const oopMax =
    serviceResult?.oop != null
      ? Number.parseFloat(String(serviceResult.oop).replace(/[^0-9.-]/g, '')) || fin.outOfPocketMax
      : fin.outOfPocketMax

  const oopRemaining =
    serviceResult?.oopRemaining != null
      ? Number.parseFloat(String(serviceResult.oopRemaining).replace(/[^0-9.-]/g, '')) ||
        fin.outOfPocketRemaining
      : fin.outOfPocketRemaining

  return {
    networkStatus: dashboard.networkInNetwork ? 'in-network' : 'out-of-network',
    coverageActive: dashboard.coverageActive ? 'yes' : 'no',
    benefitCovered,
    deductibleTotal,
    deductibleAccumulated: Math.max(0, deductibleTotal - deductibleRemaining),
    deductibleMet: deductibleMetPct(deductibleTotal, deductibleRemaining),
    oopMax,
    oopAccumulated: Math.max(0, oopMax - oopRemaining),
    coinsurancePct,
    coveragePct,
    copay: Number.isFinite(copay) ? copay : 0,
    priorAuthRequired: dashboard.authorization.required ? 'yes' : 'no',
    unitsRemaining: 4,
  }
}
